import { o as EngineErrorCode } from "./subtype-BvAzLCGC.js";
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/typeof.js
function _typeof(o) {
	"@babel/helpers - typeof";
	return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function(o) {
		return typeof o;
	} : function(o) {
		return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o;
	}, _typeof(o);
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/toPrimitive.js
function toPrimitive(t, r) {
	if ("object" != _typeof(t) || !t) return t;
	var e = t[Symbol.toPrimitive];
	if (void 0 !== e) {
		var i = e.call(t, r || "default");
		if ("object" != _typeof(i)) return i;
		throw new TypeError("@@toPrimitive must return a primitive value.");
	}
	return ("string" === r ? String : Number)(t);
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/toPropertyKey.js
function toPropertyKey(t) {
	var i = toPrimitive(t, "string");
	return "symbol" == _typeof(i) ? i : i + "";
}
//#endregion
//#region \0@oxc-project+runtime@0.140.0/helpers/esm/defineProperty.js
function _defineProperty(e, r, t) {
	return (r = toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
		value: t,
		enumerable: !0,
		configurable: !0,
		writable: !0
	}) : e[r] = t, e;
}
//#endregion
//#region src/errors/EngineError.ts
/**
* Single error class shared across engine implementations. Has a stable
* machine-readable `code` (see EngineErrorCode) so callers can match without
* sniffing message strings.
*/
var EngineError = class EngineError extends Error {
	constructor(code, message, opts = {}) {
		super(message ?? code, opts.cause === void 0 ? void 0 : { cause: opts.cause });
		_defineProperty(this, "name", "EngineError");
		_defineProperty(this, "code", void 0);
		_defineProperty(this, "details", void 0);
		this.code = code;
		this.details = opts.details;
	}
	static is(value, code) {
		if (!(value instanceof EngineError) && value?.name !== "EngineError") return false;
		if (code === void 0) return true;
		return value.code === code;
	}
	toJSON() {
		return {
			name: "EngineError",
			code: this.code,
			message: this.message,
			details: this.details
		};
	}
};
function serializeError(err) {
	if (EngineError.is(err)) return err.toJSON();
	if (err instanceof Error) return {
		name: "EngineError",
		code: err.name === "AbortError" ? EngineErrorCode.Aborted : EngineErrorCode.Unknown,
		message: err.message
	};
	return {
		name: "EngineError",
		code: EngineErrorCode.Unknown,
		message: String(err ?? "unknown error")
	};
}
function deserializeError(payload) {
	return new EngineError(payload.code, payload.message, { details: payload.details });
}
//#endregion
//#region src/search/fold.ts
/**
* Text folding for literal search: the deterministic normalization applied
* to BOTH the page text and the needle so that "Café" finds "cafe" and a
* line-wrapped "hello\n  world" finds "hello world".
*
* Fold version 1:
*   - whitespace runs collapse to a single space (any `\s`, including the
*     spaces some compatibility decompositions emit),
*   - each code point is NFKD-decomposed (ligatures split: "ﬁ" → "fi",
*     "²" → "2"),
*   - combining marks are stripped unless `keepMarks`,
*   - case is folded via upper→lower round-trip unless `keepCase` (this
*     poor-man's full fold catches "ß"→"ss" and "ς"→"σ", which a plain
*     `toLowerCase` misses).
*
* Every folded code unit remembers which original code point produced it
* (`map`), so match ranges found in folded space translate back to exact
* original-text ranges — the property the whole anchor stage rests on.
*
* Pre-folded corpus artifacts store this fold's output; bump
* `SEARCH_FOLD_VERSION` on ANY semantic change so stored corpora are
* rebuilt instead of silently mismatching fresh needles.
*/
/** Version stamp for persisted pre-folded corpus artifacts. */
const SEARCH_FOLD_VERSION = 1;
const WHITESPACE = /\s/;
const MARKS = /\p{M}/gu;
function foldText(original, options = {}) {
	const units = [];
	const map = [];
	let lastWasSpace = false;
	let index = 0;
	for (const cp of original) {
		let piece;
		if (WHITESPACE.test(cp)) piece = " ";
		else {
			piece = cp.normalize("NFKD");
			if (!options.keepMarks) piece = piece.replace(MARKS, "");
			if (!options.keepCase) piece = piece.toUpperCase().toLowerCase();
		}
		for (let u = 0; u < piece.length; u++) {
			const unit = piece[u];
			if (WHITESPACE.test(unit)) {
				if (lastWasSpace) continue;
				units.push(" ");
				map.push(index);
				lastWasSpace = true;
			} else {
				units.push(unit);
				map.push(index);
				lastWasSpace = false;
			}
		}
		index += cp.length;
	}
	return {
		folded: units.join(""),
		map: Uint32Array.from(map),
		original
	};
}
/** Code-unit length of the code point starting at `index` (1 or 2). */
function codePointLengthAt(text, index) {
	const unit = text.charCodeAt(index);
	if (unit >= 55296 && unit <= 56319 && index + 1 < text.length) {
		const next = text.charCodeAt(index + 1);
		if (next >= 56320 && next <= 57343) return 2;
	}
	return 1;
}
/**
* Translate a match found in folded space back to the original text.
* The range covers whole original code points: a hit on either folded
* half of "ﬁ" spans the full ligature, and a hit ending on a collapsed
* space extends only through the first whitespace char of the run (the
* remainder is presentation, not match).
*/
function toOriginalRange(folded, foldedStart, foldedLength) {
	const start = folded.map[foldedStart];
	const lastOriginal = folded.map[foldedStart + foldedLength - 1];
	return {
		start,
		length: lastOriginal + codePointLengthAt(folded.original, lastOriginal) - start
	};
}
//#endregion
export { deserializeError as a, EngineError as i, foldText as n, serializeError as o, toOriginalRange as r, _defineProperty as s, SEARCH_FOLD_VERSION as t };

//# sourceMappingURL=fold-CB4fAOOy.js.map